/**
 * Step2 subpage that is used for `components/subpages/migration.jsx` when a visitors is at the `step2` phase of the migration process.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/migration/step3.jsx
 * @class MigrationStep3
 * @since 0.0.1
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Checkbox} from '@rmwc/checkbox';
import {Button} from '@rmwc/button';
import {LinearProgress} from '@rmwc/linear-progress';
import {Confirm} from '../../SvgIcons';
import bnFormat from '../../../lib/helpers/bn-format';
import {explorerUrl} from '../../../lib/constants/external-wallet';
import langNavigate from '../../../lib/helpers/lang-navigate';
import translations from '../../../lib/translations';

const initialCheckeditems = {
    balance: false,
    notReversable: false,
    agreement: false,
    ownership: false
};

class Steps3 extends Component {
    constructor(props) {
        super(props);
        this.myRef = React.createRef();
        const instance = this;
        instance.props = props;
        instance.state = {
            agreed: [],
            checked: 0,
            checkedItems: {...initialCheckeditems}
        };
        instance.handleAgreement = instance.handleAgreement.bind(instance);
        instance.handleMigrate = instance.handleMigrate.bind(instance);
        instance.translate = translations.init(props);
    }

    /**
   * Method for marking agreement as accepted
   * @method handleAgreement
   * @param {*} evt
   * @since 0.0.2
   */
    handleAgreement(evt) {
        const instance = this,
            item = evt.target.name,
            isChecked = evt.target.checked,
            checkedItems = {...instance.state.checkedItems};

        checkedItems[item] = isChecked;
        instance.setState({checkedItems});

        if (evt.currentTarget.checked === true) {
            instance.setState((prevState) => ({checked: prevState.checked + 1}));
        }
        else {
            instance.setState((prevState) => ({checked: prevState.checked - 1}));
        }
    }

    /**
   * Method for redirecting users to their new account balance on explorer
   * @method handleRedirectExplorer
   * @param {*} evt
   * @since 0.0.2
   */

    handleRedirectExplorer() {
        const props = this.props,
            address = props.gen3Address,
            url = `${explorerUrl}/address/${address}/internal_transactions`;
        window.open(url, '_blank');
    }

    handleAnotherWalletMigration() {
        const props = this.props,
            reset = props.resetState();
        this.setState({checked: 0, checkedItems: {...initialCheckeditems}});
        if (reset) {
            langNavigate(props.langInfo, '/coin-migration');
        }
    }

    handleMigrate() {
        this.props.handleMigrate();
        window.scrollTo(0, this.myRef.current.offsetTop - 80);
    }

    render() {
        const instance = this,
            props = instance.props,
            state = instance.state,
            translate = instance.translate,
            locales = props.langInfo && props.langInfo.locales;
        let confirm,
            amountToTransfer,
            migrationStatus,
            claimErrorMessages,
            completeMigration;

        if (props.step3 && props.hasBalance) {
            confirm = <Confirm />;
        }

        if (props.balance !== '') {
            amountToTransfer = (
                <span>
                    {translate.t('AMOUNT_TO_MIGRATE', true)}: {bnFormat(locales, props.balance)} NRG
                </span>
            );
        }

        if (props.loading) {
            migrationStatus = (
                <div className="migration-status">
                    <span>{translate.t('MIGRATING', true)} {bnFormat(locales, props.balance)} NRG {translate.t('TO')} {props.gen3Address}</span>
                    <LinearProgress className="loader" />
                </div>
            );
        }

        if (props.claimError) {
            claimErrorMessages = (
                <span className="error">{props.claimError}</span>
            );
        }

        if (props.isClaimed) {
            completeMigration = (
                <>
                    <div className="success_message">
                        <p> <span className="uppercase">{translate.t('SUCCESS')}</span>! {bnFormat(locales, props.balance)} NRG {translate.t('MIGRATED_TO')} <span>{props.gen3Address}</span></p>
                    </div>
                    <div className="after_migration_actions">
                        <Button className="btn"
                            label={translate.t('MIGRATE_AGAIN')}
                            onClick={() => this.handleAnotherWalletMigration()}
                            unelevated />
                        <Button className="btn"
                            label={translate.t('CHECK_NEW_BALANCE')}
                            onClick={() => this.handleRedirectExplorer()}
                            unelevated />
                    </div>
                </>
            );
        }

        const isActive = props.step === 3 ? 'active' : null;
        const isApproved = (props.step3 && props.hasBalance) ? 'approved' : null;
        return (
            <div className="step3" ref={this.myRef}>
                <div className="step-head">
                    <h3 className={`${isActive} ${isApproved}`}>{translate.t('STEP3')}.</h3>
                    {confirm}
                </div>

                {props.step === 3 && (
                    <div className="details">
                        {!props.loading && (
                            <>
                                <div className="instruction">
                                    <p>{translate.t('DOUBLE_CHECK', true)} &quot;<span className="uppercase">{translate.t('START_MIGRATION')}</span>&quot;, {translate.t('NO_UNDO')}!</p>
                                </div>
                                <div className="transfer-info">{amountToTransfer}</div>
                            </>
                        )}

                        {!props.loading && (
                            <div className="confirmation">
                                <p>
                                    {translate.t('GEN3_RECIEVE_ADDRESS', true)}: <span>{props.gen3Address}</span>
                                </p>
                                <Checkbox
                                    checked={state.checkedItems.balance}
                                    disabled={props.isClaimed}
                                    name="balance"
                                    onChange={this.handleAgreement}>
                                    {translate.t('CONFIRM_BALANCE', true)}.
                                </Checkbox>
                                <Checkbox
                                    checked={state.checkedItems.notReversable}
                                    disabled={props.isClaimed}
                                    name="notReversable"
                                    onChange={this.handleAgreement}>
                                    {translate.t('UNDERSTANDING_IRREVERSABLE', true)}.
                                </Checkbox>
                                <Checkbox
                                    checked={state.checkedItems.ownership}
                                    disabled={props.isClaimed}
                                    name="ownership"
                                    onChange={this.handleAgreement}>
                                    {translate.t('GEN3_ADDRESS', true)} <span>{props.gen3Address}</span> {translate.t('IS_SAFE_OWN',)}.
                                </Checkbox>
                                <Checkbox
                                    checked={state.checkedItems.agreement}
                                    disabled={props.isClaimed}
                                    name="agreement"
                                    onChange={this.handleAgreement}>
                                    {translate.t('AGREE_MIGRATION', true)} {bnFormat(locales, props.balance)} NRG {translate.t('FROM_TO_WALLETS')}: <span>{props.gen3Address}.</span>
                                </Checkbox>
                            </div>
                        )}

                        {migrationStatus}
                        {claimErrorMessages}

                        <div className="buttons">
                            <Button
                                className="button-step3"
                                disabled={
                                    (state.checked !== 4) ||
                                    props.isClaimed ||
                                    props.loading ||
                                    !props.hasBalance
                                }
                                label={translate.t('START_MIGRATION')}
                                onClick={this.handleMigrate}
                                unelevated/>
                            <Button
                                className="button-step3"
                                disabled={
                                    props.loading
                                }
                                label={translate.t('BACK')}
                                onClick={() => props.handleChangeStep(2)}
                                unelevated/>
                        </div>
                    </div>
                )}
                {completeMigration}
            </div>
        );
    }
}

export default Steps3;

Steps3.defaultProps = {
    loading: false,
    isClaimed: false,
};

Steps3.propTypes = {
    balance: PropTypes.string.isRequired,
    claimError: PropTypes.string,
    completeStep3: PropTypes.func.isRequired,
    gen3Address: PropTypes.string.isRequired,
    handleChangeStep: PropTypes.func.isRequired,
    handleMigrate: PropTypes.func.isRequired,
    hasBalance: PropTypes.bool.isRequired,
    isClaimed: PropTypes.bool,
    loading: PropTypes.bool,
    step: PropTypes.number.isRequired,
    step3: PropTypes.bool.isRequired,
};
